import { CloseIcon } from '@atgfr8/icons';
import {
  createSignal,
  createMemo,
  Show,
  onCleanup,
  JSX,
  ParentComponent,
  createEffect,
} from 'solid-js';

const [openBottomSheetCount, setOpenBottomSheetCount] = createSignal(0);
const isAnyBottomSheetOpen = () => openBottomSheetCount() > 0;
export { isAnyBottomSheetOpen };

interface BottomSheetProps {
  title?: string;
  triggerText?: string;
  trigger?: JSX.Element;
  triggerClass?: string;
  showClose?: boolean;
  children?: JSX.Element;
  isOpen?: boolean;
  onClose?: (trigger?: string) => void;
  ref?: (api: BottomSheetAPI) => void;
  disabled?: boolean;
}

export interface BottomSheetAPI {
  open: () => void;
  close: () => void;
}

const BottomSheet: ParentComponent<BottomSheetProps> = (props) => {
  const [internalIsOpen, setInternalIsOpen] = createSignal(false);
  const [isDragging, setIsDragging] = createSignal(false);
  const [dragOffset, setDragOffset] = createSignal(0);
  const [isAnimating, setIsAnimating] = createSignal(false);
  const supportsDynamicViewportHeight =
    typeof CSS !== 'undefined' && CSS.supports('height', '1dvh');

  let backdropRef: HTMLDivElement | undefined;
  let sheetRef: HTMLDivElement | undefined;
  let startY = 0;
  let currentY = 0;

  const isOpen = createMemo(() =>
    props.isOpen !== undefined ? props.isOpen : internalIsOpen(),
  );
  const sheetHeight = () => (supportsDynamicViewportHeight ? '85dvh' : '85vh');

  createEffect(() => {
    if (isOpen()) {
      setIsAnimating(true);
      setOpenBottomSheetCount((c) => c + 1);
      // Reset animation flag after animation completes
      setTimeout(() => setIsAnimating(false), 300);
    } else {
      setOpenBottomSheetCount((c) => Math.max(0, c - 1));
    }
  });

  const openSheet = () => {
    if (props.isOpen === undefined) {
      setInternalIsOpen(true);
    }
    document.body.style.overflow = 'hidden';
  };

  const closeSheet = (trigger?: string) => {
    props.onClose?.(trigger);
    if (props.isOpen === undefined) {
      setInternalIsOpen(false);
    }
    document.body.style.overflow = '';
    setDragOffset(0);
  };

  // Expose API to parent via ref callback
  createEffect(() => {
    if (props.ref) {
      props.ref({ open: openSheet, close: closeSheet });
    }
  });

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === backdropRef) {
      closeSheet('backdrop');
    }
  };

  // Touch/Mouse drag handlers
  const handleDragStart = (clientY: number) => {
    setIsDragging(true);
    startY = clientY;
  };

  const handleDragMove = (clientY: number) => {
    if (!isDragging()) return;

    currentY = clientY;
    const diff = currentY - startY;

    // Only allow dragging down (positive values)
    if (diff > 0) {
      setDragOffset(diff);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging()) return;

    setIsDragging(false);

    // If dragged down more than 150px, close the sheet
    if (dragOffset() > 150) {
      closeSheet();
    } else {
      // Snap back to original position
      setDragOffset(0);
    }
  };

  // Touch events
  const onTouchStart = (e: TouchEvent) => {
    handleDragStart(e.touches[0].clientY);
  };

  const onTouchMove = (e: TouchEvent) => {
    handleDragMove(e.touches[0].clientY);
  };

  const onTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse events
  const onMouseDown = (e: MouseEvent) => {
    handleDragStart(e.clientY);
  };

  const onMouseMove = (e: MouseEvent) => {
    handleDragMove(e.clientY);
  };

  const onMouseUp = () => {
    handleDragEnd();
  };

  // Add/remove mouse move listeners
  const addMouseListeners = () => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const removeMouseListeners = () => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  onCleanup(() => {
    document.body.style.overflow = '';
    removeMouseListeners();
  });

  return (
    <>
      {/* Trigger Button (only show if no external control) */}
      {props.isOpen === undefined && (
        <>
          {props.trigger ? (
            <div
              onClick={() => {
                if (props.disabled) return;
                openSheet();
              }}
              class={`${props.triggerClass || ''} `}
            >
              {props.trigger}
            </div>
          ) : null}
        </>
      )}

      {/* Bottom Sheet Modal */}
      <Show when={isOpen()}>
        <div
          ref={backdropRef}
          class="fixed inset-0 bg-black/10 backdrop-blur-xs z-50 flex items-end"
          classList={{
            'animate-fade-in': isAnimating(),
          }}
          onClick={handleBackdropClick}
          style={{
            opacity: dragOffset() > 0 ? 1 - dragOffset() / 400 : 1,
          }}
        >
          <div
            ref={sheetRef}
            class="bg-white w-full rounded-t-lg flex flex-col shadow-2xl md:max-w-lg md:mx-auto"
            classList={{
              'animate-slide-up': isAnimating(),
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              height: sheetHeight(),
              transform: `translateY(${dragOffset()}px)`,
              transition: isDragging() ? 'none' : 'transform 0.3s ease-out',
            }}
          >
            <div
              class="py-3 flex justify-center cursor-grab active:cursor-grabbing shrink-0 touch-none select-none"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onMouseDown={(e) => {
                onMouseDown(e);
                addMouseListeners();
              }}
            >
              <div class="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div class="px-2 pb-2 overflow-y-auto flex-1">
              {(props.title || props.showClose !== false) && (
                <div class="flex justify-between items-center px-2 mb-5 text-black">
                  <h2 class="text-lg font-semibold">{props.title || ''}</h2>
                  {props.showClose !== false && (
                    <CloseIcon onClick={() => closeSheet()} />
                  )}
                </div>
              )}

              <div class="flex flex-col gap-4 text-black">{props.children}</div>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
};

export default BottomSheet;
